/**
 * Urgent countdown timer (FOMO panier) — convention Smootify native.
 *
 * Structure HTML supportée :
 *
 *   <urgent-cart-countdown
 *      data-time="15"            <!-- durée en MINUTES (convention Smootify) -->
 *      data-on-end="clear"       <!-- clear|redirect|nothing -->
 *      data-redirect-to="/">
 *     <div data-prop="minutes">15</div>
 *     <div data-prop="seconds">00</div>
 *   </urgent-cart-countdown>
 *
 * Démarrage : au 1er ajout au panier (smootify:added_to_cart). Le startTime est
 * persisté dans localStorage `lmdr-cart-countdown` pour survivre aux reloads.
 *
 * Tick chaque seconde, hydrate `[data-prop="minutes"]` et `[data-prop="seconds"]`.
 *
 * À 0 :
 *   - data-on-end="clear"    → window.Smootify.clearCart() + reset
 *   - data-on-end="redirect" → window.location.href = data-redirect-to
 *   - data-on-end="nothing"  → arrête le tick, ne fait rien
 *
 * Au cart vide → reset le timer.
 */

import { getCurrentCart, clearCart } from "../cart/state.js";

const STORAGE_KEY = "lmdr-cart-countdown";

interface CountdownState {
  startTime: number; // ms epoch
  durationSec: number; // toujours stocké en SECONDES en interne
}

function readState(): CountdownState | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CountdownState;
    if (typeof parsed.startTime !== "number" || typeof parsed.durationSec !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeState(state: CountdownState | null): void {
  if (typeof localStorage === "undefined") return;
  try {
    if (state === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* noop */
  }
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function renderTime(wrappers: NodeListOf<HTMLElement>, secondsLeft: number): void {
  const totalMin = Math.floor(secondsLeft / 60);
  const sec = secondsLeft % 60;
  const hours = Math.floor(totalMin / 60);
  const min = totalMin % 60;

  wrappers.forEach((w) => {
    // Convention Smootify : [data-prop="minutes"|"seconds"]
    w.querySelectorAll<HTMLElement>('[data-prop="minutes"]').forEach((el) => {
      el.textContent = pad2(hours > 0 ? min : totalMin);
    });
    w.querySelectorAll<HTMLElement>('[data-prop="seconds"]').forEach((el) => {
      el.textContent = pad2(sec);
    });
    w.querySelectorAll<HTMLElement>('[data-prop="hours"]').forEach((el) => {
      el.textContent = pad2(hours);
    });
    // Fallback / convention legacy : [data-countdown-minutes|seconds]
    w.querySelectorAll<HTMLElement>("[data-countdown-minutes]").forEach((el) => {
      el.textContent = pad2(hours > 0 ? min : totalMin);
    });
    w.querySelectorAll<HTMLElement>("[data-countdown-seconds]").forEach((el) => {
      el.textContent = pad2(sec);
    });
    w.querySelectorAll<HTMLElement>("[data-countdown-hours]").forEach((el) => {
      el.textContent = pad2(hours);
    });
  });
}

function handleEnd(wrapper: HTMLElement): void {
  const onEnd = wrapper.getAttribute("data-on-end") ?? "clear";
  if (onEnd === "clear") {
    void clearCart();
    writeState(null);
  } else if (onEnd === "redirect") {
    const url = wrapper.getAttribute("data-redirect-to") ?? "/";
    writeState(null);
    if (typeof window !== "undefined") window.location.href = url;
  } else {
    // "nothing" : juste reset le state, garde l'affichage à 00:00
    writeState(null);
  }
}

let tickHandle: number | undefined;

/**
 * Lit la durée du countdown en SECONDES depuis l'attribut `data-time` du wrapper.
 *
 * Convention Smootify : `data-time` est en MINUTES sur `<urgent-cart-countdown>`.
 * Convention legacy LMDR : `data-time` en SECONDES sur `[data-countdown]`.
 *
 * Détection : si tag = `URGENT-CART-COUNTDOWN`, on suppose minutes ; sinon secondes.
 */
function readDurationSec(wrapper: HTMLElement): number {
  const raw = Number.parseInt(wrapper.getAttribute("data-time") ?? "300", 10);
  if (!Number.isFinite(raw) || raw <= 0) return 300;
  if (wrapper.tagName === "URGENT-CART-COUNTDOWN") return raw * 60;
  return raw;
}

function findWrappers(doc: Document): NodeListOf<HTMLElement> {
  return doc.querySelectorAll<HTMLElement>(
    "urgent-cart-countdown, [data-countdown]",
  );
}

function tick(doc: Document): void {
  const wrappers = findWrappers(doc);
  if (wrappers.length === 0) {
    stopTick();
    return;
  }

  const state = readState();
  if (!state) {
    // Pas de timer actif → cache les wrappers et stop
    wrappers.forEach((w) => w.classList.add("lmdr-countdown-inactive"));
    renderTime(wrappers, 0);
    stopTick();
    return;
  }

  const elapsed = (Date.now() - state.startTime) / 1000;
  const remaining = Math.max(0, Math.ceil(state.durationSec - elapsed));

  wrappers.forEach((w) => w.classList.remove("lmdr-countdown-inactive"));
  renderTime(wrappers, remaining);

  if (remaining <= 0) {
    wrappers.forEach((w) => handleEnd(w));
    stopTick();
  }
}

function startTick(doc: Document): void {
  if (tickHandle !== undefined) return;
  tick(doc); // immediate
  tickHandle = window.setInterval(() => tick(doc), 1000);
}

function stopTick(): void {
  if (tickHandle !== undefined) {
    window.clearInterval(tickHandle);
    tickHandle = undefined;
  }
}

function initFromCartIfAny(doc: Document): void {
  const wrappers = findWrappers(doc);
  if (wrappers.length === 0) return;

  const cart = getCurrentCart();
  const hasItems = (cart?.totalQuantity ?? 0) > 0;

  if (!hasItems) {
    writeState(null);
    // Re-render à 00:00 + cache
    tick(doc);
    return;
  }

  // Cart non vide : si pas de state existant, démarre maintenant.
  const state = readState();
  if (!state) {
    const durationSec = readDurationSec(wrappers[0]!);
    writeState({ startTime: Date.now(), durationSec });
  }
  startTick(doc);
}

export function attachCountdown(doc: Document = document): void {
  if (typeof document === "undefined") return;
  if ((document as unknown as { __lmdrCountdownBound?: boolean }).__lmdrCountdownBound) return;
  (document as unknown as { __lmdrCountdownBound?: boolean }).__lmdrCountdownBound = true;

  document.addEventListener("smootify:added_to_cart", () => {
    initFromCartIfAny(doc);
  });

  document.addEventListener("smootify:cart_updated", () => {
    initFromCartIfAny(doc);
  });

  // Au boot : restore le timer si state localStorage existe + cart non vide
  initFromCartIfAny(doc);
}
